import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Send, ShieldAlert } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ModuleTitleCOMPASS } from "@/components/compass/ModuleTitleCOMPASS";
import DirectFeedbackHistoryPanel from "@/components/rhythm/DirectFeedbackHistoryPanel";

const GAP_CATEGORIES = [
  { key: "quality_of_work", label: "Quality of work" },
  { key: "reliability_conduct", label: "Reliability / attendance / conduct" },
  { key: "communication", label: "Communication / responsiveness" },
  { key: "policy_compliance", label: "Policy compliance" },
  { key: "other", label: "Other" },
];

export default function CompassEntryForm() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [reports, setReports] = useState([]);
  const [form, setForm] = useState({
    employee_id: "",
    gap_description: "",
    evidence_notes: "",
    prior_feedback_notes: "",
    gap_category: "reliability_conduct",
    criteria_affirmations: [],
    anti_retaliation_attestation: false,
    po_consulted: false,
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [s, dir] = await Promise.all([
          api.get("/compass/settings"),
          api.get("/users/directory"),
        ]);
        setSettings(s.data);
        // direct reports only for a manager; PO/Super Admin see all
        const isMgrPlus = ["manager", "po_admin", "super_admin"].includes(user.role);
        setReports(dir.data.filter((u) => u.id !== user.id && (
          user.role === "manager" ? u.manager_id === user.id : true
        )));
        setForm((f) => ({
          ...f,
          criteria_affirmations: (s.data.entry_criteria || []).map((c) => ({
            key: c.key, text: c.text, affirmed: false,
          })),
        }));
      } catch (err) {
        toast.error(formatApiErrorDetail(err?.response?.data?.detail));
      }
    })();
  }, [user]);

  const allAffirmed = form.criteria_affirmations.length >= 5 && form.criteria_affirmations.every((c) => c.affirmed);
  const canSubmit = form.employee_id && form.gap_description.trim() && form.evidence_notes.trim()
    && form.prior_feedback_notes.trim() && form.gap_category
    && allAffirmed && form.anti_retaliation_attestation && form.po_consulted;

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/compass/entry-requests", {
        employee_id: form.employee_id,
        gap_description: form.gap_description.trim(),
        evidence_notes: form.evidence_notes.trim(),
        prior_feedback_notes: form.prior_feedback_notes.trim(),
        gap_category: form.gap_category,
        criteria_affirmations: form.criteria_affirmations,
        anti_retaliation_attestation: form.anti_retaliation_attestation,
        po_consulted: form.po_consulted,
      });
      toast.success("Entry request submitted. People Operations will review it.");
      navigate("/compass");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const toggle = (idx) => setForm((f) => ({
    ...f,
    criteria_affirmations: f.criteria_affirmations.map((c, i) => i === idx ? { ...c, affirmed: !c.affirmed } : c),
  }));

  if (!settings) return <p className="italic text-charcoal-muted">Loading…</p>;

  return (
    <div className="space-y-6" data-testid="compass-entry-form">
      <Link to="/compass" className="patria-btn-ghost -ml-3">
        <ChevronLeft className="h-4 w-4" strokeWidth={1.5} /> COMPASS home
      </Link>
      <div>
        <ModuleTitleCOMPASS />
        <h1 className="mt-3 text-4xl font-serif text-forest-900">Entry Request</h1>
        <p className="mt-2 max-w-2xl text-charcoal-soft">
          A plan cannot be built until this entry is submitted and People Operations concurs.
          Please answer truthfully and specifically.
        </p>
      </div>

      {/* Two-track reminder */}
      <div className="rounded-2xl border border-terracotta-500/30 bg-terracotta-500/5 p-5" data-testid="compass-two-track-notice">
        <p className="flex items-center gap-2 text-terracotta-700 font-medium">
          <ShieldAlert className="h-4 w-4" strokeWidth={1.5} /> Two-track boundary
        </p>
        <p className="mt-1 text-sm text-charcoal-soft leading-relaxed">
          COMPASS is <strong>only</strong> for documented work performance or conduct.
          Spiritual growth, calling, and covenant matters are pastoral and handled on a
          separate track — please do not enter them into a COMPASS record.
        </p>
      </div>

      <div className="patria-card p-6 space-y-5">
        <Field label="Employee (direct report)" required>
          <select
            className="patria-input w-full sm:max-w-md"
            value={form.employee_id}
            onChange={(e) => setForm({ ...form, employee_id: e.target.value })}
            data-testid="entry-employee"
          >
            <option value="">Select a direct report…</option>
            {reports.map((u) => (
              <option key={u.id} value={u.id}>{`${u.name} — ${u.job_title || "—"}`}</option>
            ))}
          </select>
        </Field>
        <Field label="Gap category" required>
          <select
            className="patria-input w-full sm:max-w-md"
            value={form.gap_category}
            onChange={(e) => setForm({ ...form, gap_category: e.target.value })}
            data-testid="entry-category"
          >
            {GAP_CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
        </Field>
        <Field label="Gap description (specific, documented)" required>
          <textarea
            className="patria-input w-full min-h-[120px]"
            value={form.gap_description}
            onChange={(e) => setForm({ ...form, gap_description: e.target.value })}
            placeholder="Cite exact behaviors or outcomes falling short, with dates and examples."
            data-testid="entry-gap"
          />
        </Field>
        <Field label="Evidence (dates, examples)" required>
          <textarea
            className="patria-input w-full min-h-[90px]"
            value={form.evidence_notes}
            onChange={(e) => setForm({ ...form, evidence_notes: e.target.value })}
            data-testid="entry-evidence"
          />
        </Field>
        <Field label="Direct feedback already given (what/when/outcome)" required>
          <textarea
            className="patria-input w-full min-h-[90px]"
            value={form.prior_feedback_notes}
            onChange={(e) => setForm({ ...form, prior_feedback_notes: e.target.value })}
            data-testid="entry-prior-feedback"
          />
        </Field>
      </div>

      {/* Cross-module: RHYTHM direct-feedback history */}
      {form.employee_id && <DirectFeedbackHistoryPanel employeeId={form.employee_id} />}

      {/* Entry criteria checklist */}
      <div className="patria-card p-6">
        <h2 className="text-xl font-serif text-forest-900">Entry Criteria — All Five Must Be Affirmed</h2>
        <ul className="mt-4 space-y-3">
          {form.criteria_affirmations.map((c, i) => (
            <li key={c.key}>
              <label className="flex items-start gap-3 text-sm">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={c.affirmed}
                  onChange={() => toggle(i)}
                  data-testid={`entry-criterion-${c.key}`}
                />
                <span>{c.text}</span>
              </label>
            </li>
          ))}
        </ul>
      </div>

      {/* Anti-retaliation */}
      <div className="patria-card p-6 border-l-4 border-terracotta-500">
        <h2 className="text-xl font-serif text-forest-900">Anti-Retaliation Attestation</h2>
        <p className="mt-2 text-sm text-charcoal-soft">{settings.retaliation_attestation_text}</p>
        <label className="mt-4 flex items-start gap-3 text-sm">
          <input
            type="checkbox"
            className="mt-1"
            checked={form.anti_retaliation_attestation}
            onChange={(e) => setForm({ ...form, anti_retaliation_attestation: e.target.checked })}
            data-testid="entry-attestation"
          />
          <span>I affirm the above.</span>
        </label>
        <label className="mt-3 flex items-start gap-3 text-sm">
          <input
            type="checkbox"
            className="mt-1"
            checked={form.po_consulted}
            onChange={(e) => setForm({ ...form, po_consulted: e.target.checked })}
            data-testid="entry-po-consulted"
          />
          <span>I have consulted People Operations before submitting this entry.</span>
        </label>
      </div>

      <div className="flex justify-end">
        <button
          className="patria-btn-primary"
          onClick={submit}
          disabled={!canSubmit || submitting}
          data-testid="entry-submit"
        >
          <Send className="h-4 w-4" strokeWidth={1.5} />
          {submitting ? "Submitting…" : "Submit for PO review"}
        </button>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-widest font-medium text-charcoal-soft mb-1.5">
        {label} {required && <span className="text-terracotta-500">*</span>}
      </label>
      {children}
    </div>
  );
}
